import { useQuery } from '@tanstack/react-query';
import { type ReactNode, useEffect, useState } from 'react';
import { api, type WireLine } from '@/api/client';
import { envStyle, lineTime } from '@/lib/format';
import type { Command } from '@/lib/shell';
import { Icon } from './Icon';

// ⌘K: paste an exception UUID (or its first 8 characters), a request or a user id, and it is found across 30 days and
// every environment; or jump to an environment, a range, live, privacy.
export function Palette(props: { commands: Command[]; onFound: (line: WireLine) => void; onClose: () => void }): ReactNode {
    const [input, setInput] = useState('');
    const [needle, setNeedle] = useState('');
    const [index, setIndex] = useState(0);
    useEffect(() => {
        const timer = setTimeout(() => setNeedle(/^[\w-]{6,64}$/.test(input.trim()) ? input.trim() : ''), 220);

        return () => clearTimeout(timer);
    }, [input]);
    const found = useQuery({ queryKey: ['find', needle], queryFn: ({ signal }) => api.find(needle, signal), enabled: needle !== '' });

    const results = (found.data?.results ?? []).map((line) => ({
        icon: line.level === 'error' ? '✕' : '●',
        label: `${line.matched === 'error_uuid' ? 'Exception' : line.matched === 'request_id' ? 'Request' : 'User'} · ${line.exception || line.message.slice(0, 60)}`,
        sub: `${envStyle(line.env).label} · ${line.service} · ${lineTime(line.ts).clock} Rome · ${line.ts.slice(0, 10)}`,
        run: () => props.onFound(line),
    }));
    const words = input.trim().toLowerCase();
    const commands = props.commands.filter((command) => words === '' || command.label.toLowerCase().includes(words));
    const items: Array<{ icon: string; label: string; sub: string; group: string; key?: string; run: () => void }> = [
        ...results.map((result) => ({ ...result, group: 'Found' })),
        ...commands.map((command) => ({ ...command, sub: '', group: words ? 'Commands' : 'Go and do' })),
    ];
    const active = Math.min(index, Math.max(0, items.length - 1));

    return (
        <div className="scrim" onMouseDown={(event) => event.target === event.currentTarget && props.onClose()}>
            <div className="palette float">
                <div className="pin">
                    <Icon name="search" size={19} />
                    <input
                        autoFocus
                        value={input}
                        placeholder="Paste an exception id, a request or a user id, or jump anywhere"
                        onChange={(event) => {
                            setInput(event.target.value);
                            setIndex(0);
                        }}
                        onKeyDown={(event) => {
                            if (event.key === 'ArrowDown') {
                                event.preventDefault();
                                setIndex((active + 1) % Math.max(1, items.length));
                            } else if (event.key === 'ArrowUp') {
                                event.preventDefault();
                                setIndex((active - 1 + items.length) % Math.max(1, items.length));
                            } else if (event.key === 'Enter') {
                                items[active]?.run();
                                props.onClose();
                            } else if (event.key === 'Escape') {
                                props.onClose();
                            }
                        }}
                    />
                </div>
                <div className="pl">
                    {found.isFetching && <div className="pg">Searching 30 days…</div>}
                    {needle && !found.isFetching && results.length === 0 && <div className="pg">Nothing found for “{needle}”</div>}
                    {items.map((item, i) => (
                        <div key={`${item.group}-${i}`}>
                            {(i === 0 || items[i - 1]?.group !== item.group) && <div className="pg">{item.group}</div>}
                            <button
                                className={`pi ${i === active ? 'on' : ''}`}
                                onMouseEnter={() => setIndex(i)}
                                onClick={() => {
                                    item.run();
                                    props.onClose();
                                }}
                            >
                                <span className="ico">{item.icon}</span>
                                <span>
                                    {item.label}
                                    {item.sub && <div className="sub">{item.sub}</div>}
                                </span>
                                {item.key && <span className="kbd k">{item.key}</span>}
                            </button>
                        </div>
                    ))}
                </div>
                <div className="pfoot">
                    <span>
                        <span className="kbd">↑↓</span> move
                    </span>
                    <span>
                        <span className="kbd">↵</span> open
                    </span>
                    <span>
                        <span className="kbd">Esc</span> close
                    </span>
                </div>
            </div>
        </div>
    );
}
